/**
 * Match-log reachability.
 * The badge follows Arena itself (GET /api/ping) and the match hub.
 * A general internet probe must not paint the badge online.
 *
 * - arenaReachable: fewer than two missed pings in a row. Gates the outbox.
 * - hubPhase: connected / reconnecting / disconnected. Does not gate the outbox.
 * - link state: online, reconnecting, or unreachable.
 * The browser online event wakes a ping and the hub. It does not paint the badge.
 */

export const ARENA_PING_PATH = '/api/ping';
export const ARENA_PING_INTERVAL_MS = 3000;
export const ARENA_PING_TIMEOUT_MS = 2500;
export const ARENA_MISSES_BEFORE_UNREACHABLE = 2;

/** New query each ping so a wedged response is not reused. The server ignores it. */
export const arenaPingUrl = (nowMs: number): string => `${ARENA_PING_PATH}?t=${nowMs}`;

export type HubPhase = 'connected' | 'reconnecting' | 'disconnected';
export type ArenaLinkState = 'online' | 'reconnecting' | 'unreachable';

type OnlineListener = (online: boolean) => void;
type LinkListener = (state: ArenaLinkState) => void;

export const recordPingResult = (
    missStreak: number,
    ok: boolean
): { missStreak: number; reachable: boolean } => {
    if (ok) {
        return { missStreak: 0, reachable: true };
    }

    const next = missStreak + 1;
    return {
        missStreak: next,
        reachable: next < ARENA_MISSES_BEFORE_UNREACHABLE,
    };
};

export const linkState = (arenaReachable: boolean, hub: HubPhase): ArenaLinkState => {
    if (!arenaReachable) return 'unreachable';
    if (hub !== 'connected') return 'reconnecting';
    return 'online';
};

export const linkLabel = (state: ArenaLinkState): string => {
    if (state === 'online') return 'Online';
    if (state === 'reconnecting') return 'Reconnecting';
    return 'Arena unreachable';
};

export const pendingLabel = (count: number): string => `${count} pending`;

export const pingArena = async (
    fetchImpl: typeof fetch,
    timeoutMs: number,
    external?: AbortSignal
): Promise<boolean> => {
    if (external?.aborted) return false;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const onExternal = (): void => ctrl.abort();
    external?.addEventListener('abort', onExternal);
    try {
        const response = await fetchImpl(arenaPingUrl(Date.now()), {
            method: 'GET',
            cache: 'no-store',
            credentials: 'same-origin',
            signal: ctrl.signal,
        });
        if (external?.aborted) return false;
        return response.ok;
    } catch {
        return false;
    } finally {
        clearTimeout(timer);
        external?.removeEventListener('abort', onExternal);
    }
};

export class ConnectivityMonitor {
    private arenaReachable = true;

    private missStreak = 0;

    private hubPhase: HubPhase = 'disconnected';

    private listeners = new Set<OnlineListener>();

    private linkListeners = new Set<LinkListener>();

    private lastLink: ArenaLinkState = 'reconnecting';

    private watchers = 0;

    private loopToken = 0;

    private wake: (() => void) | null = null;

    /** Bumped when the browser comes back so an aborted ping is not a miss. */
    private pingGeneration = 0;

    private pingAbort: AbortController | null = null;

    private onlineBound = false;

    /** Hub restart. The badge color does not change here. */
    private recoveryListeners = new Set<() => void>();

    /** True when Arena has not missed two pings in a row. */
    public getIsOnline = (): boolean => this.arenaReachable;

    /** Outbox may POST only when Arena answered recently. Hub state is separate. */
    public getCanFlush = (): boolean => this.arenaReachable;

    public getLinkState = (): ArenaLinkState => linkState(this.arenaReachable, this.hubPhase);

    public subscribe = (listener: OnlineListener): (() => void) => {
        this.listeners.add(listener);
        listener(this.arenaReachable);
        return (): void => {
            this.listeners.delete(listener);
        };
    };

    public subscribeLink = (listener: LinkListener): (() => void) => {
        this.linkListeners.add(listener);
        listener(this.getLinkState());
        return (): void => {
            this.linkListeners.delete(listener);
        };
    };

    /**
     * Fires on the browser `online` event while a match log is open.
     * Wakes a ping and restarts the hub. It does not paint the badge.
     */
    public subscribeRecovery = (listener: () => void): (() => void) => {
        this.recoveryListeners.add(listener);
        return (): void => {
            this.recoveryListeners.delete(listener);
        };
    };

    /**
     * Ping /api/ping while at least one match log is open.
     * Returns a release function for component unmount.
     */
    public retainMatchLog = (): (() => void) => {
        this.watchers += 1;
        if (this.watchers === 1) {
            this.bindOnline();
            this.loopToken += 1;
            void this.pingLoop(this.loopToken);
        }

        let released = false;
        return (): void => {
            if (released) return;
            released = true;
            this.watchers = Math.max(0, this.watchers - 1);
            if (this.watchers === 0) {
                this.unbindOnline();
                this.loopToken += 1;
                this.pingAbort?.abort();
                this.wake?.();
            }
        };
    };

    /** Match hub reports its transport state. Does not by itself allow a flush. */
    public setHubPhase = (phase: HubPhase): void => {
        if (this.hubPhase === phase) return;
        this.hubPhase = phase;
        this.emitLink();
    };

    /** Call after any successful Arena event HTTP. */
    public forceOnline = (): void => {
        this.missStreak = 0;
        this.setArenaReachable(true);
    };

    /** One ping now (Sync now / reconnect drain). A single miss does not flip the badge. */
    public probeArenaNow = async (): Promise<boolean> => {
        const ok = await pingArena(fetch, ARENA_PING_TIMEOUT_MS);
        this.applyPing(ok);
        return ok;
    };

    /** Kept so existing callers still start the monitor. Pings run only while a match log is open. */
    public ensureStarted = (): void => {
        // retainMatchLog owns the ping loop.
    };

    private pingLoop = async (token: number): Promise<void> => {
        while (token === this.loopToken && this.watchers > 0) {
            const generation = this.pingGeneration;
            const started = Date.now();
            const ctrl = new AbortController();
            this.pingAbort = ctrl;
            const ok = await pingArena(fetch, ARENA_PING_TIMEOUT_MS, ctrl.signal);
            if (this.pingAbort === ctrl) this.pingAbort = null;
            if (token !== this.loopToken) return;
            // Browser came back and aborted this attempt. Do not count the miss.
            if (generation !== this.pingGeneration) continue;
            this.applyPing(ok);
            const elapsed = Date.now() - started;
            const wait = Math.max(0, ARENA_PING_INTERVAL_MS - elapsed);
            await this.delay(wait);
        }
    };

    private bindOnline = (): void => {
        if (this.onlineBound || typeof window === 'undefined') return;
        window.addEventListener('online', this.handleBrowserOnline);
        this.onlineBound = true;
    };

    private unbindOnline = (): void => {
        if (!this.onlineBound || typeof window === 'undefined') return;
        window.removeEventListener('online', this.handleBrowserOnline);
        this.onlineBound = false;
    };

    /**
     * The browser online flag must not paint the badge. This only drops a hung
     * ping and asks the hub to drop a hung negotiate so the next ping can
     * use a live socket.
     */
    private handleBrowserOnline = (): void => {
        this.recoveryListeners.forEach((listener) => listener());
        this.nudge();
    };

    private nudge = (): void => {
        this.pingGeneration += 1;
        this.pingAbort?.abort();
        this.wake?.();
    };

    private delay = (ms: number): Promise<void> =>
        new Promise((resolve) => {
            let settled = false;
            const finish = (): void => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                if (this.wake === finish) this.wake = null;
                resolve();
            };
            const timer = setTimeout(finish, ms);
            this.wake = finish;
        });

    private applyPing = (ok: boolean): void => {
        const next = recordPingResult(this.missStreak, ok);
        this.missStreak = next.missStreak;
        this.setArenaReachable(next.reachable);
    };

    private setArenaReachable = (next: boolean): void => {
        const changed = this.arenaReachable !== next;
        this.arenaReachable = next;
        if (changed) {
            this.listeners.forEach((listener) => listener(next));
        }
        this.emitLink();
    };

    private emitLink = (): void => {
        const state = this.getLinkState();
        if (state === this.lastLink) return;
        this.lastLink = state;
        this.linkListeners.forEach((listener) => listener(state));
    };

}

export const connectivityMonitor = new ConnectivityMonitor();
